import { useCallback, useMemo, useRef } from 'react';
import {
  isConfigApiError,
  getRequiredDomainEntityType,
} from '@web-config-app/core';
import type {
  EnvironmentKey,
  EntityDetailSchema,
  ConfigEntityDetail,
  EntityConfigEndpoint,
  EntityBulkOperationStatus,
  EntityIndexedDBEntry,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { captureError } from '@leagueplatform/observability';
import { isProduction } from '@leagueplatform/app-environment';
import { useLoadEntityRelationshipsSchemas } from '../use-load-entity-relationships-schemas/use-load-entity-relationships-schemas';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import { useFindConfigEntityByDomainId } from '../use-find-config-entity-by-domain-id/use-find-config-entity-by-domain-id';
import {
  getAllReferencedEntities,
  type DiscoveryFailure,
} from '../../utilities/get-all-referenced-entities/get-all-referenced-entities';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { useEntityCreate } from '../use-entity-create/use-entity-create';
import { useConfigApp } from '../use-config-app/use-config-app';
import { prepareEntityBodyData } from '../../utilities/prepare-entity-body-data/prepare-entity-body-data';
import {
  assertCompletionCriteriaPublishable,
  CompletionCriteriaGuardError,
} from '../../utilities/assert-completion-criteria-publishable/assert-completion-criteria-publishable';
import { useCopyEntitiesToEnvironmentIndexedDB } from '../use-copy-entities-to-environment-indexed-db/use-copy-entities-to-environment-indexed-db';
import {
  batchAsyncOperations,
  isRetryableError,
} from '../../utilities/batch-async-operations/batch-async-operations';
import {
  REDUCED_RATE_LIMIT_INTERVAL_MS,
  shouldUseReducedRateLimit,
} from '../../utilities/reduced-rate-limit/reduced-rate-limit';
import {
  createBulkActionReportingStore,
  type BulkActionReportingStore,
} from '../../bulk-action-reporting/bulk-action-reporting.store';
import type { BulkActionSession } from '../../bulk-action-reporting/bulk-action-reporting.types';
import { withBestEffortReporting } from '../../bulk-action-reporting/with-best-effort-reporting';
import { buildOperationErrorDetail } from '../../utilities/build-operation-error-detail/build-operation-error-detail';

const BULK_COPY_RATE_LIMIT = {
  intervalMs: 1000,
  intervalCap: 10,
} as const;

const BULK_COPY_REDUCED_RATE_LIMIT = {
  intervalMs: REDUCED_RATE_LIMIT_INTERVAL_MS,
  intervalCap: 1,
} as const;

const BULK_COPY_CONCURRENCY = 10;
const BULK_COPY_MAX_RETRIES = 3;
const BULK_COPY_RETRY_DELAY_MS = 5000;

/**
 * Props for the {@link useCopyEntitiesToEnvironment} hook.
 */
export interface UseCopyEntitiesToEnvironmentProps {
  /** The environment key from which entities will be read/copied */
  sourceEnvironment: EnvironmentKey;
  /** The environment key to which entities will be copied */
  targetEnvironment: EnvironmentKey;
  /** The name to which entities will be copied */
  targetEnvironmentName: string;
  /** The JSON schema describing the entity structure, used for resolving relationships */
  schema: EntityDetailSchema;
  /** The domain entity type key (e.g. 'config-activity') used for reporting */
  domainEntityType: string;
  /**
   * Optional function to preprocess each root entity before copying.
   * Use this to refetch entities when the input data may be stale (e.g., from selection state).
   * If not provided, root entities are used as-is.
   *
   * @param entity - The root entity to preprocess
   * @returns Promise resolving to the preprocessed entity, or undefined to skip the entity
   */
  preprocessEntity?: (
    entity: ConfigEntityDetail,
  ) => Promise<ConfigEntityDetail | undefined>;
  /**
   * The tenant ID to capture errors with.
   * @param tenantId - The tenant ID to capture errors with.
   */
  tenantId: string;
  /**
   * Optional Zustand store for session-based reporting. When provided by a
   * parent that subscribes to the store, copy progress is streamed to the UI.
   */
  bulkActionReportingStore?: BulkActionReportingStore;
}

/**
 * A promotion target chosen per operation rather than when a hook was called.
 *
 * `name` is required: it's the label the copy reports against, and every producer already has
 * it. Optional invited a fallback that showed the raw environment key as a display name.
 */
export interface CopyTargetOverride {
  environmentKey: EnvironmentKey;
  name: string;
}

/**
 * Recovers the target a reporting session was opened against.
 *
 * The session is the only record of where a run actually went that every hook instance can
 * read, which is what a retry needs -- see the call in `retryFailedCopy`. Returns undefined
 * unless both halves are present, since a target without its label would report the retry
 * against a different environment name than the run it resumes.
 */
export const getCopyTargetFromSession = (
  session: BulkActionSession | undefined,
): CopyTargetOverride | undefined =>
  session?.targetEnvironment && session.metadata?.targetEnvironmentName
    ? {
        environmentKey: session.targetEnvironment,
        name: session.metadata.targetEnvironmentName,
      }
    : undefined;

export interface EntityCopyResult {
  status: EntityBulkOperationStatus;
  error?: string;
}
export type CopyEntitiesToEnvironmentSuccessData = {
  configEntities: EntityIndexedDBEntry[];
  rootEntities: Array<ConfigEntityDetail>;
  /**
   * Failures that occurred during reference discovery.
   * These represent entities that were referenced but could not be fetched
   * or resolved from the source environment. Callers should surface these
   * as warnings to users so they're aware of potentially incomplete copies.
   */
  discoveryFailures: DiscoveryFailure[];
};

const shouldUseReducedCopyRateLimit = (entities: ConfigEntityDetail[]) =>
  entities.some((entity) =>
    shouldUseReducedRateLimit(
      entity.attributes?.entityMetadata?.domainEntityType,
    ),
  );

/**
 * A hook that copies one or more config entities from a source environment to a
 * target environment. This operation recursively discovers all referenced entities
 * (via relationship fields) and copies them along with the root entities.
 *
 * The hook returns an async callback that, when invoked with an array of root
 * {@link ConfigEntityDetail} objects, will:
 *
 * 1. **Find references** – Traverse each root entity to collect all transitively
 *    referenced entities from the source environment.
 * 2. **Copy entities** – Create each entity (root + referenced) on the target
 *    environment. Entities that already exist (409 or "already exists" error) are
 *    marked as `'skipped'`; other failures are marked as `'error'`; successes are
 *    marked as `'complete'`.
 *
 * Progress is reported via the optional `bulkActionReportingStore` prop.
 *
 * @param props - Configuration for the copy to environment operation
 * @param props.sourceEnvironment - Environment key to read entities from
 * @param props.targetEnvironment - Environment key to copy entities to
 * @param props.targetEnvironmentName - Name of the target environment
 * @param props.schema - Entity detail schema used for relationship resolution
 *
 * @returns An async function that accepts an array of root {@link ConfigEntityDetail}
 * objects and returns a promise resolving to a {@link CopyEntitiesToEnvironmentSuccessData}:
 * ```ts
 * {
 *   configEntities: EntityIndexedDBEntry[]; // root + referenced entities, ordered, with per-entity status
 *   rootEntities: ConfigEntityDetail[];     // the original roots passed in
 *   discoveryFailures: DiscoveryFailure[];  // references that could not be fetched/resolved
 * }
 * ```
 *
 * @example
 * ```tsx
 * const copyEntities = useCopyEntitiesToEnvironment({
 *   sourceEnvironment: 'staging',
 *   targetEnvironment: 'production',
 *   schema: myEntitySchema,
 * });
 *
 * const handleCopy = async () => {
 *   const { configEntities, discoveryFailures } = await copyEntities([selectedEntity]);
 *   const errors = configEntities.filter((entry) => entry.status === 'error');
 *   if (errors.length) {
 *     console.error('Some entities failed to copy', errors);
 *   }
 *   if (discoveryFailures.length) {
 *     console.warn('Some references could not be resolved', discoveryFailures);
 *   }
 * };
 * ```
 */
export const useCopyEntitiesToEnvironment = ({
  sourceEnvironment,
  domainEntityType,
  targetEnvironmentName: configuredTargetEnvironmentName,
  targetEnvironment: configuredTargetEnvironment,
  schema,
  preprocessEntity,
  tenantId,
  bulkActionReportingStore,
}: UseCopyEntitiesToEnvironmentProps) => {
  const { formatMessage } = useIntl();
  const loadEntityRelationshipsSchemas = useLoadEntityRelationshipsSchemas();
  const findEntityOnSourceByDomainEntityId = useFindConfigEntityByDomainId({
    environment: sourceEnvironment,
  });
  const fetchEntityOnSourceById = useConfigEntityGetByIdQuery({
    sourceEnvironment,
    domainEntityType,
  });
  const { configEntity } = useConfigApp();
  const { mutateAsync: createEntity } = useEntityCreate({
    environment: configuredTargetEnvironment,
    entity: configEntity,
    operation: 'copyToEnvironment',
  });

  const {
    addEntity,
    getEntityEntryById,
    getOrderedEntities,
    updateEntityStatus,
    clearEntities,
  } = useCopyEntitiesToEnvironmentIndexedDB();

  const reportingStore = useMemo(
    () => bulkActionReportingStore ?? createBulkActionReportingStore(),
    [bulkActionReportingStore],
  );

  const { createAbortController, abort: cancelCopy } = useAbortController();

  // Fallback for retries when reporting never opened a session to read the target back off.
  const activeTargetOverrideRef = useRef<CopyTargetOverride | undefined>(
    undefined,
  );

  const copyEntitiesToEnvironment = useCallback(
    async (
      rootEntities: Array<ConfigEntityDetail>,
      /**
       * Copy here instead of the configured environment.
       *
       * Per call rather than per hook so a caller with several targets can act on whichever
       * the author just picked, without waiting a render for the hook to rebind. Everything
       * below reads the resolved values, so creates, IndexedDB entries and the reporting
       * session all describe one target.
       */
      targetOverride?: CopyTargetOverride,
    ) => {
      activeTargetOverrideRef.current = targetOverride;
      const targetEnvironment =
        targetOverride?.environmentKey ?? configuredTargetEnvironment;
      const targetEnvironmentName =
        targetOverride?.name ?? configuredTargetEnvironmentName;
      const abortController = createAbortController();

      // Clear IndexedDB for a fresh start. When called from retryFailedCopy,
      // this wipes previously successful entities from IndexedDB — that's OK
      // because the reporting store retains overall session state and only the
      // retried subset goes through discovery + copy here.
      await clearEntities();
      const activeBulkCopyRateLimit = shouldUseReducedCopyRateLimit(
        rootEntities,
      )
        ? BULK_COPY_REDUCED_RATE_LIMIT
        : BULK_COPY_RATE_LIMIT;

      const hasExistingSession = Boolean(reportingStore.getState().session);
      if (!hasExistingSession) {
        withBestEffortReporting(() => {
          reportingStore.getState().createSession({
            operationType: 'copyToEnvironment',
            domainEntityType,
            sourceEnvironment,
            targetEnvironment,
            metadata: { tenantId, targetEnvironmentName },
          });

          rootEntities.forEach((entity) => {
            const entityMetadata = entity.attributes?.entityMetadata;
            reportingStore.getState().recordItemResult({
              sourceEntityId: entity.id,
              entityType: entityMetadata?.domainEntityType ?? '',
              entityName: entityMetadata?.name,
              domainEntityId: entityMetadata?.domainEntityId,
              status: 'loading',
            });
          });
        });
      }

      // Collect all discovery failures across all root entities
      const allDiscoveryFailures: DiscoveryFailure[] = [];

      const getResponse = (configEntities: EntityIndexedDBEntry[]) =>
        ({
          configEntities,
          rootEntities,
          discoveryFailures: allDiscoveryFailures,
        } satisfies CopyEntitiesToEnvironmentSuccessData);

      // Step 1: Discover all referenced entities, adding each entity to IndexedDB.
      // continueOnError ensures a single discovery failure doesn't abort the
      // entire batch — the failed entity is recorded as an error and the rest
      // continue to be processed.
      await batchAsyncOperations({
        items: rootEntities,
        abortSignal: abortController.signal,
        continueOnError: true,
        maxRetries: BULK_COPY_MAX_RETRIES,
        retryDelayMs: BULK_COPY_RETRY_DELAY_MS,
        shouldRetry: isRetryableError,
        operation: async (rootEntity) => {
          let rootEntityData: ConfigEntityDetail | undefined;

          // If preprocessEntity is provided, use it to get fresh data
          // Otherwise, use the entity as-is (caller has fresh data)
          if (preprocessEntity) {
            // Errors propagate to batchAsyncOperations which retries
            // retryable failures (5xx, 429, network). The onItemFailed
            // handler records the final error after all retries are exhausted.
            rootEntityData = await preprocessEntity(rootEntity);

            if (!rootEntityData) {
              // If preprocessing returned undefined, skip this entity
              const errorMsg = 'Entity could not be fetched';
              await addEntity(rootEntity, {
                targetEnvironment,
                dependencies: [],
                status: 'error',
                error: errorMsg,
              });
              withBestEffortReporting(() => {
                const entityMetadata = rootEntity.attributes?.entityMetadata;
                reportingStore.getState().recordItemResult({
                  sourceEntityId: rootEntity.id,
                  entityType: entityMetadata?.domainEntityType ?? '',
                  entityName: entityMetadata?.name,
                  domainEntityId: entityMetadata?.domainEntityId,
                  status: 'error',
                  error: { detail: errorMsg },
                });
              });
              return;
            }
          } else {
            // No preprocessing needed, use the entity as-is
            rootEntityData = rootEntity;
          }

          const {
            entities: referencedEntitiesMap,
            failures: discoveryFailures,
          } = await getAllReferencedEntities({
            entityData: rootEntityData,
            findEntityOnSourceByDomainEntityId,
            fetchEntityOnSourceById,
            loadEntityRelationshipsSchemas,
            entitySchema: schema,
          });

          // The discovery result includes the root entity; remove it since
          // this flow handles the root separately (added to IndexedDB below).
          if (rootEntityData.id) {
            referencedEntitiesMap.delete(rootEntityData.id);
          }

          // Track any discovery failures for this root entity
          if (discoveryFailures.length > 0) {
            allDiscoveryFailures.push(...discoveryFailures);

            // Log discovery failures to Sentry for monitoring in prod
            if (isProduction()) {
              discoveryFailures.forEach((failure) => {
                captureError(new Error(failure.message), {
                  errorName: 'Copy to Environment Discovery Failure',
                  tags: {
                    failureType: failure.type,
                    sourceEnvironment,
                    targetEnvironment,
                    domainEntityType: failure.domainEntityType,
                    tenantId,
                  },
                  context: {
                    DiscoveryFailure: {
                      type: failure.type,
                      domainEntityId: failure.domainEntityId,
                      domainEntityType: failure.domainEntityType,
                      configEntityId: failure.configEntityId,
                      referencedFrom: failure.referencedFrom,
                      rootEntityId: rootEntity.id,
                    },
                  },
                });
              });
            }
          }

          // Get root entity's dependencies from all referenced entities
          // For topological sorting, listing all transitive dependencies ensures
          // the root entity is processed last (after all its dependencies)
          const rootDependencies = Array.from(
            referencedEntitiesMap.values(),
          ).map(({ entity: refEntity }) => ({
            id: refEntity.id,
            domainEntityType: getRequiredDomainEntityType(
              refEntity,
              'addEntity',
            ),
          }));

          // Add root entity to IndexedDB with its direct dependencies
          await addEntity(rootEntityData, {
            targetEnvironment,
            dependencies: rootDependencies,
          });

          // Add each referenced entity to IndexedDB with ITS dependencies
          const referencedEntitiesArray = Array.from(
            referencedEntitiesMap.values(),
          );

          // Add each referenced entity sequentially using reduce
          // upsertEntity handles duplicates gracefully, so no need to check for existing entries
          await referencedEntitiesArray.reduce(
            async (prevPromise, { entity, dependencies: deps }) => {
              await prevPromise;
              // Add this entity to IndexedDB with its own dependencies
              await addEntity(entity, {
                targetEnvironment,
                dependencies: deps,
              });
              return Promise.resolve();
            },
            Promise.resolve(),
          );
        },
        concurrency: BULK_COPY_CONCURRENCY,
        rateLimit: activeBulkCopyRateLimit,
        onItemFailed: async ({ item: rootEntity, error }) => {
          // Discovery failed for this root entity (e.g. getAllReferencedEntities
          // threw). Record it in IndexedDB and the reporting store so it isn't
          // silently lost.
          const entityError = buildOperationErrorDetail(error, formatMessage);
          await addEntity(rootEntity, {
            targetEnvironment,
            dependencies: [],
            status: 'error',
            error: entityError.detail,
          });
          withBestEffortReporting(() => {
            const entityMetadata = rootEntity.attributes?.entityMetadata;
            reportingStore.getState().recordItemResult({
              sourceEntityId: rootEntity.id,
              entityType: entityMetadata?.domainEntityType ?? '',
              entityName: entityMetadata?.name,
              domainEntityId: entityMetadata?.domainEntityId,
              status: 'error',
              error: entityError,
            });
          });
        },
      });

      // Add discovery failures to IndexedDB as error entries for visibility.
      // This ensures they appear in the final status counts.
      // Skip failures whose ID matches an entity already in IndexedDB — this can
      // happen when multiple root entities reference the same config entity and one
      // root's traversal succeeded while another's failed (due to a transient error).
      if (allDiscoveryFailures.length > 0) {
        await Promise.all(
          allDiscoveryFailures.map(async (failure) => {
            const failureId =
              failure.configEntityId ||
              `${failure.domainEntityType}:${failure.domainEntityId}`;

            // Don't overwrite an entity that was already successfully discovered
            const existing = await getEntityEntryById(failureId);
            if (existing) {
              return;
            }

            await addEntity(
              {
                id: failureId,
                type: 'config-entity',
                attributes: {
                  entityMetadata: {
                    domainEntityType: failure.domainEntityType || 'unknown',
                    domainEntityId: failure.domainEntityId,
                    status: 'new',
                  },
                  domainEntityAttributes: { entityMetadata: { status: 'new' } },
                },
              },
              {
                targetEnvironment,
                dependencies: [],
                status: 'error',
                error: failure.message,
              },
            );
          }),
        );
      }

      // Get fresh ordered entities directly from IndexedDB AFTER all entities have been added
      // (useLiveQuery values may be stale due to React closure capture)
      const allOrderedEntities = await getOrderedEntities();
      // Entities that could not be fetched from the source environment (even
      // after retries) have no data to copy — exclude them from the copy phase.
      const orderedEntities = allOrderedEntities.filter(
        (entry) => entry.status !== 'error',
      );
      // Step 2: Copy entities to the target environment using per-entity operations with limited concurrency and rate limiting, and collect the results
      if (orderedEntities && orderedEntities.length > 0) {
        withBestEffortReporting(() => {
          orderedEntities.forEach(({ data: entity }) => {
            reportingStore.getState().recordItemResult({
              sourceEntityId: entity.id,
              entityType:
                entity.attributes?.entityMetadata?.domainEntityType ?? '',
              entityName: entity.attributes?.entityMetadata?.name,
              domainEntityId: entity.attributes?.entityMetadata?.domainEntityId,
              status: 'loading',
            });
          });

          // Set the total from the session's actual item count — this includes
          // fetch-phase failures (copy-all), discovery failures, and all items
          // entering the copy phase, avoiding undercounting from Math.max.
          const totalItems =
            reportingStore.getState().session?.itemResults.length ?? 0;
          reportingStore.getState().updateTotalItems(totalItems);
        });

        await batchAsyncOperations({
          items: orderedEntities,
          abortSignal: abortController.signal,
          operation: async ({ data: entity }) => {
            const entityMetadata = entity.attributes?.entityMetadata;
            const entityDomainType = entityMetadata?.domainEntityType ?? '';

            // Only the API create should be inside the try/catch that
            // controls retries. Bookkeeping (IndexedDB status updates and
            // reporting-store writes) is isolated so that a failure there
            // cannot cause batchAsyncOperations to re-issue createEntity.
            try {
              // A copy creates the entity outright, so this is the last point to
              // stop it. Published only, matching the backend — a draft is not served.
              if (entityMetadata?.status === 'published') {
                assertCompletionCriteriaPublishable({
                  data: entity,
                  domainEntityType: entityDomainType,
                });
              }

              const bodyData = prepareEntityBodyData({
                data: entity,
                entity: configEntity,
                operationEndpoint: configEntity.endpoints
                  .create as EntityConfigEndpoint,
              });
              const bodyInit = JSON.stringify({ data: bodyData });
              await createEntity({
                body: bodyInit,
                domainEntityType: entityDomainType,
                environment: targetEnvironment,
              });
            } catch (error) {
              // 409 means the entity already exists on the target — mark as
              // skipped and don't propagate so it won't be retried.
              if (isConfigApiError(error) && error.status === 409) {
                await updateEntityStatus(entity.id, 'skipped');
                withBestEffortReporting(() => {
                  reportingStore.getState().recordItemResult({
                    sourceEntityId: entity.id,
                    entityType: entityDomainType,
                    entityName: entityMetadata?.name,
                    domainEntityId: entityMetadata?.domainEntityId,
                    status: 'skipped',
                  });
                });
                return;
              }
              // All other errors propagate so batchAsyncOperations can retry
              // retryable ones (5xx, 429, network errors).
              throw error;
            }

            // Bookkeeping after successful create — failures here are
            // swallowed so they don't trigger a retry of the API call.
            try {
              await updateEntityStatus(entity.id, 'complete');
            } catch (statusError) {
              captureError(
                statusError instanceof Error
                  ? statusError
                  : new Error(
                      `Failed to update entity status for ${entity.id}`,
                    ),
              );
            }
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entity.id,
                entityType: entityDomainType,
                entityName: entityMetadata?.name,
                domainEntityId: entityMetadata?.domainEntityId,
                status: 'success',
              });
            });
          },
          concurrency: BULK_COPY_CONCURRENCY,
          rateLimit: activeBulkCopyRateLimit,
          continueOnError: true,
          maxRetries: BULK_COPY_MAX_RETRIES,
          retryDelayMs: BULK_COPY_RETRY_DELAY_MS,
          // isRetryableError would read the status-less refusal as a network fault.
          shouldRetry: (error) =>
            !(error instanceof CompletionCriteriaGuardError) &&
            isRetryableError(error),
          onItemRetrying: async ({ item: { data: entity } }) => {
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entity.id,
                entityType:
                  entity.attributes?.entityMetadata?.domainEntityType ?? '',
                entityName: entity.attributes?.entityMetadata?.name,
                domainEntityId:
                  entity.attributes?.entityMetadata?.domainEntityId,
                status: 'loading',
              });
            });
          },
          onItemFailed: async ({ item: { data: entity }, error }) => {
            const entityMetadata = entity.attributes?.entityMetadata;
            const entityDomainType = entityMetadata?.domainEntityType ?? '';
            const operationError = buildOperationErrorDetail(
              error,
              formatMessage,
            );
            try {
              await updateEntityStatus(
                entity.id,
                'error',
                operationError.detail,
              );
            } catch (updateError) {
              captureError(
                updateError instanceof Error
                  ? updateError
                  : new Error(
                      `Failed to update entity status for ${entity.id}`,
                    ),
              );
            }
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entity.id,
                entityType: entityDomainType,
                entityName: entityMetadata?.name,
                domainEntityId: entityMetadata?.domainEntityId,
                status: 'error',
                error: operationError,
              });
            });
          },
        });

        // Only complete the session if this invocation created it.
        // When called from retryFailedCopy, the parent owns the session
        // and is responsible for completing it.
        if (!hasExistingSession) {
          withBestEffortReporting(() => {
            reportingStore.getState().completeSession();
          });
        }
      } else if (!hasExistingSession) {
        // All entities failed during discovery/preprocessing — nothing to
        // copy, but we still need to set the total and complete the session.
        withBestEffortReporting(() => {
          const totalItems =
            reportingStore.getState().session?.itemResults.length ?? 0;
          reportingStore.getState().updateTotalItems(totalItems);
          reportingStore.getState().completeSession();
        });
      }

      return getResponse(allOrderedEntities);
    },
    [
      createAbortController,
      configEntity,
      createEntity,
      fetchEntityOnSourceById,
      findEntityOnSourceByDomainEntityId,
      loadEntityRelationshipsSchemas,
      schema,
      formatMessage,
      configuredTargetEnvironmentName,
      preprocessEntity,
      addEntity,
      getEntityEntryById,
      configuredTargetEnvironment,
      sourceEnvironment,
      getOrderedEntities,
      updateEntityStatus,
      clearEntities,
      domainEntityType,
      tenantId,
      reportingStore,
    ],
  );

  const retryFailedCopy = useCallback(async () => {
    const { session } = reportingStore.getState();

    if (!session) {
      return;
    }

    const hasErrorItems = session.itemResults.some(
      (item) => item.status === 'error',
    );
    if (!hasErrorItems) {
      return;
    }

    // Read off the session rather than the ref, because the retry is not always driven by
    // the hook instance that ran the copy: a failed "copy selected" is retried through the
    // copy-all hook, whose own ref was never set. That instance would fall back to its
    // configured target and write into an environment nobody picked.
    //
    // The ref still covers the gap where reporting failed to open a session, since the
    // target is then recorded nowhere else.
    const sessionTarget =
      getCopyTargetFromSession(session) ?? activeTargetOverrideRef.current;

    // Build a lookup from entity ID → entity type so we can pass the
    // correct per-item type when refetching (dependency entities may
    // have a different type from the hook-level domainEntityType).
    const entityTypeByIdMap = new Map(
      session.itemResults.map((item) => [item.sourceEntityId, item.entityType]),
    );

    const { retriedEntityIds } = reportingStore
      .getState()
      .resumeSessionForRetry();

    if (retriedEntityIds.length === 0) {
      return;
    }

    const abortController = createAbortController();

    const throwIfAborted = () => {
      if (abortController.signal.aborted) {
        throw new DOMException('Operation was cancelled', 'AbortError');
      }
    };

    // Re-fetch failed entities from the source environment.
    // Each fetch checks the abort signal after completing so that
    // cancelled operations are recognised as early as possible (the
    // underlying fetchQuery API does not accept an AbortSignal).
    const results = await Promise.allSettled(
      retriedEntityIds.map((entityId) => {
        throwIfAborted();
        const perItemEntityType = entityTypeByIdMap.get(entityId);
        return fetchEntityOnSourceById(entityId, perItemEntityType).then(
          ({ data }) => {
            throwIfAborted();
            return data;
          },
          (error) => {
            withBestEffortReporting(() => {
              const entityError = buildOperationErrorDetail(
                error,
                formatMessage,
              );
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: perItemEntityType ?? domainEntityType,
                status: 'error',
                error: entityError,
              });
            });
            throw error;
          },
        );
      }),
    );

    throwIfAborted();

    const refetchedEntities = results
      .filter(
        (result): result is PromiseFulfilledResult<ConfigEntityDetail> =>
          result.status === 'fulfilled',
      )
      .map((result) => result.value);

    if (refetchedEntities.length === 0) {
      withBestEffortReporting(() => {
        reportingStore.getState().completeSession();
      });
      return;
    }

    await copyEntitiesToEnvironment(refetchedEntities, sessionTarget);

    // copyEntitiesToEnvironment skips completeSession when an existing
    // session is present, so the retry caller is responsible.
    withBestEffortReporting(() => {
      reportingStore.getState().completeSession();
    });
  }, [
    reportingStore,
    createAbortController,
    copyEntitiesToEnvironment,
    fetchEntityOnSourceById,
    formatMessage,
    domainEntityType,
  ]);

  return { copyEntitiesToEnvironment, cancelCopy, retryFailedCopy };
};
